import {createSlice, type PayloadAction} from "@reduxjs/toolkit";
import {authReducerActions} from "./authReducer.ts";
import {type FilterBarSelection} from "../model/filterBar/FilterBarDefinition.ts";

// Active list-overview filters. Persisted to sessionStorage (see store/index.ts)
// so a filter survives a reload but is dropped when the tab/browser closes.
//
// They live in Redux (not the URL) on purpose: the filter is cross-page
// navigation context — it must still be active after the user dips into a
// detail/edit page and comes back. The previous approach restored URL state
// from sessionStorage in a mount effect, which was timing-fragile under the
// data router (worked on F5 but not on in-app "back"); it has been removed.
// Pagination still lives in the URL (sticky via useUrlPagination); sort is
// sticky sessionStorage state in useTableSort.
export interface OverviewFiltersState {
    product: string; // JSON filter expression for the product overview
    order: {fuzzy: string; createdAfter: string};
    coupon: {fuzzy: string};
    // filters: null = untouched, the BFF applies its defaults (#56642)
    customer: {fuzzy: string; filters: FilterBarSelection | null};
}

const initialState: OverviewFiltersState = {
    product: "[]",
    order: {fuzzy: "", createdAfter: ""},
    coupon: {fuzzy: ""},
    customer: {fuzzy: "", filters: null},
};

export const overviewFiltersSlice = createSlice({
    name: "overviewFilters",
    initialState,
    reducers: {
        setProductFilter: (state, action: PayloadAction<string>) => {
            state.product = action.payload;
        },
        setOrderFilter: (state, action: PayloadAction<{fuzzy: string; createdAfter: string}>) => {
            state.order = action.payload;
        },
        setCouponFuzzy: (state, action: PayloadAction<string>) => {
            state.coupon.fuzzy = action.payload;
        },
        setCustomerFuzzy: (state, action: PayloadAction<string>) => {
            state.customer.fuzzy = action.payload;
        },
        setCustomerFilters: (state, action: PayloadAction<FilterBarSelection | null>) => {
            state.customer.filters = action.payload;
        },
        // back to the page-load state: no search, the BFF's default filters
        // (null when no defaults are known yet, the BFF then applies them)
        resetCustomer: (state, action: PayloadAction<FilterBarSelection | null>) => {
            state.customer = {fuzzy: "", filters: action.payload};
        },
    },
    extraReducers: builder => {
        // Don't leak one user's filters into the next session on a shared tab.
        builder.addCase(authReducerActions.logoutSuccess, () => initialState);
        builder.addCase(authReducerActions.startSupportContext, () => initialState);
        builder.addCase(authReducerActions.endSupportContext, () => initialState);
    },
});

export const overviewFiltersActions = overviewFiltersSlice.actions;
export default overviewFiltersSlice.reducer;
